import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Anchor, Send, Loader2, X, Zap, Sparkles, Paperclip, Headset, LifeBuoy, Inbox, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Label } from './ui/label';
import api from '../lib/axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const TIPOS_CHAMADO = [
  { valor: 'duvida', label: 'Dúvida' },
  { valor: 'erro', label: 'Erro' },
  { valor: 'melhoria', label: 'Melhoria' },
  { valor: 'analise_informacao', label: 'Análise de Informação' },
  { valor: 'nova_funcionalidade', label: 'Nova Funcionalidade' },
];

// Sugestão proativa: identifica a página atual e oferece uma pergunta pronta e específica,
// em vez de um "Como posso ajudar?" genérico.
const CONTEXTO_PAGINAS = [
  { match: (p) => /\/embarcacao\//.test(p), sugestao: 'Dúvidas sobre os custos desta Ficha Técnica?' },
  { match: (p) => p.startsWith('/compras'), sugestao: 'Precisa de ajuda com cotações ou pedidos de compra?' },
  { match: (p) => p.startsWith('/production'), sugestao: 'Quer ajuda para entender o progresso desta produção?' },
  { match: (p) => p.startsWith('/cronograma'), sugestao: 'Dúvidas sobre as fases do cronograma?' },
  { match: (p) => p.startsWith('/crm/pipeline'), sugestao: 'Precisa de ajuda para mover uma oportunidade no pipeline?' },
  { match: (p) => p.startsWith('/crm/propostas'), sugestao: 'Dúvidas sobre como gerar ou editar uma proposta?' },
  { match: (p) => p.startsWith('/crm'), sugestao: 'Posso ajudar com algo no módulo de CRM?' },
  { match: (p) => p.startsWith('/projects'), sugestao: 'Dúvidas sobre este projeto ou seus dados financeiros?' },
  { match: (p) => p.startsWith('/fornecedores'), sugestao: 'Precisa de ajuda para cadastrar ou avaliar um fornecedor?' },
  { match: (p) => p.startsWith('/produtos'), sugestao: 'Dúvidas sobre cadastro de produtos ou controle de estoque?' },
  { match: (p) => p.startsWith('/relatorios'), sugestao: 'Precisa de ajuda para interpretar algum relatório?' },
  { match: (p) => p.startsWith('/configuracoes'), sugestao: 'Dúvidas sobre esta configuração do sistema?' },
  { match: (p) => p.startsWith('/kanban'), sugestao: 'Precisa de ajuda para organizar o quadro Kanban?' },
  { match: (p) => p === '/', sugestao: 'Posso te ajudar a entender algum indicador do Dashboard?' },
];

function getSugestaoContextual(pathname) {
  const achou = CONTEXTO_PAGINAS.find((c) => c.match(pathname));
  return achou ? achou.sugestao : 'Como posso ajudar por aqui?';
}

// Balão de Boas-Vindas Proativo: saudação curta e contextual exibida junto ao ícone flutuante
// (chat ainda fechado). Separado da sugestão mais longa acima (usada dentro do chat já aberto).
const NOMES_MODULO_SAUDACAO = [
  { match: (p) => /\/embarcacao\//.test(p), nome: 'esta Ficha Técnica' },
  { match: (p) => p.startsWith('/compras'), nome: 'compras' },
  { match: (p) => p.startsWith('/production'), nome: 'esta produção' },
  { match: (p) => p.startsWith('/cronograma'), nome: 'o cronograma' },
  { match: (p) => p.startsWith('/crm/pipeline'), nome: 'o pipeline' },
  { match: (p) => p.startsWith('/crm/propostas'), nome: 'propostas' },
  { match: (p) => p.startsWith('/crm'), nome: 'o CRM' },
  { match: (p) => p.startsWith('/projects'), nome: 'este projeto' },
  { match: (p) => p.startsWith('/fornecedores'), nome: 'fornecedores' },
  { match: (p) => p.startsWith('/produtos'), nome: 'produtos' },
  { match: (p) => p.startsWith('/relatorios'), nome: 'este relatório' },
  { match: (p) => p.startsWith('/configuracoes'), nome: 'esta configuração' },
  { match: (p) => p.startsWith('/kanban'), nome: 'o Kanban' },
];

function getSaudacaoContextual(pathname) {
  const achou = NOMES_MODULO_SAUDACAO.find((c) => c.match(pathname));
  return achou ? `Dúvidas sobre ${achou.nome}?` : 'Dúvidas? Sou o Nau, estou aqui para te ajudar!';
}

// Menu Inicial (Quick Replies): opções de triagem exibidas sempre que o chat abre sem
// mensagens ainda. "Analisar informação" força Modo Negócio (BI); as outras 3 forçam Modo Suporte.
const RESPOSTAS_RAPIDAS = [
  { emoji: '🚩', label: 'Encontrei um erro', texto: 'Encontrei um erro no sistema.', forcarSuporte: true },
  { emoji: '❓', label: 'Dúvida em funcionalidade', texto: 'Tenho uma dúvida sobre uma funcionalidade do sistema.', forcarSuporte: true },
  { emoji: '💡', label: 'Sugerir melhoria', texto: 'Quero sugerir uma melhoria para o sistema.', forcarSuporte: true },
  { emoji: '📊', label: 'Analisar informação', texto: 'Quero analisar informações do sistema: lucros, vendas e cotações.', forcarNegocio: true },
];

// Persistência do balão: no máximo 1x por dia, por página (não repetir a cada navegação).
function _chaveSaudacoesHoje() {
  return `nau_saudacoes_${new Date().toISOString().slice(0, 10)}`;
}
function jaMostrouSaudacaoHoje(pathname) {
  try {
    return JSON.parse(localStorage.getItem(_chaveSaudacoesHoje()) || '[]').includes(pathname);
  } catch {
    return false;
  }
}
function marcarSaudacaoVista(pathname) {
  try {
    const chave = _chaveSaudacoesHoje();
    const vistos = JSON.parse(localStorage.getItem(chave) || '[]');
    if (!vistos.includes(pathname)) {
      localStorage.setItem(chave, JSON.stringify([...vistos, pathname]));
    }
  } catch {
    /* localStorage indisponível - o balão apenas mostra com mais frequência, sem quebrar o app */
  }
}

export function NauFloatingButton() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [model, setModel] = useState('flash');
  const [imagemBase64, setImagemBase64] = useState(null);
  const [chamadoDialogOpen, setChamadoDialogOpen] = useState(false);
  const [chamadoTipo, setChamadoTipo] = useState('duvida');
  const [chamadoContexto, setChamadoContexto] = useState(null);
  const [criandoChamado, setCriandoChamado] = useState(false);
  const [chamadosAbertos, setChamadosAbertos] = useState({});
  const [feedbackDado, setFeedbackDado] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const greetingTimerRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Balão de Boas-Vindas Proativo: aparece no máximo 1x por dia/página, e desaparece sozinho em 5s.
  useEffect(() => {
    if (location.pathname === '/nau') return;
    if (jaMostrouSaudacaoHoje(location.pathname)) return;
    setShowGreeting(true);
    marcarSaudacaoVista(location.pathname);
    clearTimeout(greetingTimerRef.current);
    greetingTimerRef.current = setTimeout(() => setShowGreeting(false), 5000);
    return () => clearTimeout(greetingTimerRef.current);
  }, [location.pathname]);

  // ...ou desaparece assim que o usuário interagir com qualquer parte da tela.
  useEffect(() => {
    if (!showGreeting) return;
    const dismiss = () => setShowGreeting(false);
    window.addEventListener('scroll', dismiss, { passive: true });
    window.addEventListener('keydown', dismiss);
    window.addEventListener('pointerdown', dismiss);
    return () => {
      window.removeEventListener('scroll', dismiss);
      window.removeEventListener('keydown', dismiss);
      window.removeEventListener('pointerdown', dismiss);
    };
  }, [showGreeting]);

  const sugestaoContextual = getSugestaoContextual(location.pathname);
  const saudacaoContextual = getSaudacaoContextual(location.pathname);
  const usuarioLogado = JSON.parse(localStorage.getItem('nautilus_user') || '{}');
  const primeiroNome = (usuarioLogado.full_name || '').split(' ')[0] || 'visitante';

  const handleSelecionarImagem = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const tiposAceitos = ['image/png', 'image/jpeg', 'image/webp'];
    if (!tiposAceitos.includes(file.type)) {
      toast.error('Envie apenas imagens PNG, JPEG ou WEBP');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Imagem muito grande (máximo 8MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImagemBase64(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const sendMessage = async (text, opts = {}) => {
    const { forcarSuporte = false, forcarNegocio = false } = opts;
    if (!text?.trim() && !imagemBase64) return;
    const userText = text?.trim() || '(imagem anexada)';
    const imagemEnviada = imagemBase64;
    setInput('');
    setImagemBase64(null);
    setSending(true);

    const tempUserMsg = { id: 'temp-user', role: 'user', content: userText, anexo_preview: imagemEnviada };
    setMessages((prev) => [...prev, tempUserMsg]);

    try {
      const res = await api.post('/nau/chat', {
        message: userText,
        session_id: sessionId,
        model,
        forcar_modo_suporte: forcarSuporte,
        forcar_modo_negocio: forcarNegocio,
        page_context: location.pathname,
        image_base64: imagemEnviada || undefined,
      });

      const { response, session_id, modo, sugerir_chamado, tipo_sugerido, anexo_url } = res.data;
      if (!sessionId) setSessionId(session_id);

      setMessages((prev) => [
        ...prev.filter((m) => m.id !== 'temp-user'),
        { ...tempUserMsg, id: `user-${Date.now()}`, anexo_url },
        { id: res.data.message_id, role: 'assistant', content: response, modo, sugerir_chamado, tipo_sugerido },
      ]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao enviar mensagem';
      toast.error(detail);
      setMessages((prev) => prev.filter((m) => m.id !== 'temp-user'));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const abrirDialogoChamado = (msg, idxAnterior) => {
    const mensagemUsuario = idxAnterior >= 0 ? messages[idxAnterior] : null;
    setChamadoContexto({
      msgId: msg.id,
      titulo: (mensagemUsuario?.content || 'Chamado aberto via Nau IA').slice(0, 70),
      mensagem: `Pergunta: ${mensagemUsuario?.content || ''}\n\nResposta do Nau IA: ${msg.content}`,
      anexos: mensagemUsuario?.anexo_url
        ? [{ nome: 'print.png', url: mensagemUsuario.anexo_url, tipo: 'image/png', tamanho: 0 }]
        : [],
    });
    setChamadoTipo(msg.tipo_sugerido || 'duvida');
    setChamadoDialogOpen(true);
  };

  const confirmarAbrirChamado = async () => {
    if (!chamadoContexto) return;
    setCriandoChamado(true);
    try {
      await api.post('/tickets', {
        tipo: chamadoTipo,
        titulo: chamadoContexto.titulo,
        mensagem: chamadoContexto.mensagem,
        session_id: sessionId,
        page_context: `Nau IA (flutuante) - ${location.pathname}`,
        anexos: chamadoContexto.anexos || [],
      });
      setChamadosAbertos((prev) => ({ ...prev, [chamadoContexto.msgId]: true }));
      toast.success('Chamado aberto! Acompanhe em "Meus Chamados", no Nau IA.');
      setChamadoDialogOpen(false);
    } catch {
      toast.error('Erro ao abrir chamado');
    } finally {
      setCriandoChamado(false);
    }
  };

  const irParaMeusChamados = () => {
    setOpen(false);
    navigate('/nau?view=chamados');
  };

  const handleClickSaudacao = () => {
    setShowGreeting(false);
    setOpen(true);
    sendMessage(sugestaoContextual, { forcarSuporte: true });
  };

  const enviarFeedback = async (msgId, tipo) => {
    if (feedbackDado[msgId] || !sessionId) return;
    setFeedbackDado((prev) => ({ ...prev, [msgId]: tipo }));
    try {
      await api.post('/nau/feedback', { message_id: msgId, session_id: sessionId, feedback: tipo });
      if (tipo === 'negativo') toast.info('Obrigado pelo retorno — abrimos um chamado para nossa equipe revisar esta resposta.');
    } catch {
      setFeedbackDado((prev) => {
        const novo = { ...prev };
        delete novo[msgId];
        return novo;
      });
      toast.error('Erro ao registrar feedback');
    }
  };

  // Nunca mostrar o botão flutuante dentro da própria página do Nau IA (evita chat duplicado)
  if (location.pathname === '/nau') return null;

  if (!open) {
    return (
      <>
        {showGreeting && (
          <div className="fixed bottom-24 right-6 z-50 max-w-[230px] animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="nau-greeting-bubble">
            <button
              onClick={handleClickSaudacao}
              onPointerDown={(e) => e.stopPropagation()}
              className="relative block w-full text-left bg-white text-gray-700 text-sm leading-snug rounded-2xl rounded-br-md shadow-xl border border-gray-100 px-4 py-3 hover:shadow-2xl hover:-translate-y-0.5 transition-all"
            >
              {saudacaoContextual}
              <span className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white border-r border-b border-gray-100 rotate-45" />
            </button>
          </div>
        )}
        <button
          onClick={() => { setShowGreeting(false); setOpen(true); }}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center z-50 group"
          data-testid="nau-floating-btn"
          title="Precisa de ajuda? Fale com o Nau"
        >
          <Anchor className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </button>
      </>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[560px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50 overflow-hidden" data-testid="nau-floating-chat">
      {/* Header */}
      <div className="px-4 py-3 bg-blue-600 text-white flex items-center gap-3 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
          <Anchor className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Nau</p>
          <p className="text-[10px] text-white/60">Assistente Nautilus OS</p>
        </div>
        <div className="flex gap-1">
          <button
            className={`text-[10px] px-2 py-0.5 rounded-full ${model === 'flash' ? 'bg-white/20' : 'bg-white/5 hover:bg-white/10'}`}
            onClick={() => setModel('flash')}
            title="Gemini Flash (rápido)"
          >
            <Zap className="w-2.5 h-2.5 inline" />
          </button>
          <button
            className={`text-[10px] px-2 py-0.5 rounded-full ${model === 'advanced' ? 'bg-white/20' : 'bg-white/5 hover:bg-white/10'}`}
            onClick={() => setModel('advanced')}
            title="GPT-5.2 (avançado)"
          >
            <Sparkles className="w-2.5 h-2.5 inline" />
          </button>
          <button onClick={irParaMeusChamados} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 hover:bg-white/10" title="Meus Chamados" data-testid="nau-floating-meus-chamados-link">
            <Inbox className="w-2.5 h-2.5 inline" />
          </button>
        </div>
        <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded-lg" data-testid="nau-floating-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-gray-50">
        {messages.length === 0 && (
          <div className="text-center py-4 px-1" data-testid="nau-floating-quick-replies-menu">
            <Anchor className="w-8 h-8 mx-auto text-blue-300 mb-2" />
            <p className="text-sm text-gray-700 font-semibold" data-testid="nau-floating-greeting-nome">Olá, {primeiroNome}!</p>
            <p className="text-xs text-gray-400 mt-0.5 mb-3">{sugestaoContextual}</p>
            <div className="grid grid-cols-2 gap-2">
              {RESPOSTAS_RAPIDAS.map((r) => (
                <button
                  key={r.label}
                  onClick={() => sendMessage(r.texto, { forcarSuporte: r.forcarSuporte, forcarNegocio: r.forcarNegocio })}
                  className="text-xs text-left bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-xl px-2.5 py-2.5 transition-colors"
                  data-testid={`nau-floating-quick-reply-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="block text-base mb-0.5">{r.emoji}</span>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, idx) => (
          <div key={msg.id || idx} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`} data-testid={`nau-floating-msg-${idx}`}>
            {msg.role === 'assistant' && msg.modo === 'suporte' && (
              <span className="mb-1 inline-flex items-center gap-1 text-[10px] font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-1.5 py-0.5">
                <Headset className="w-2.5 h-2.5" /> Modo Suporte
              </span>
            )}
            <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-xl px-3 py-2 text-xs ${
                  msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-700'
                }`}
              >
                {(msg.anexo_preview || msg.anexo_url) && (
                  <img src={msg.anexo_preview || msg.anexo_url} alt="Print anexado" className="rounded-lg mb-1.5 max-h-32 border border-white/20" />
                )}
                {msg.role === 'assistant' ? (
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p className="mb-1 last:mb-0 text-xs leading-relaxed">{children}</p>,
                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                      ul: ({ children }) => <ul className="list-disc pl-3 mb-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal pl-3 mb-1">{children}</ol>,
                      li: ({ children }) => <li className="text-xs">{children}</li>,
                    }}
                  >
                    {msg.content}
                  </ReactMarkdown>
                ) : (
                  <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>
                )}
              </div>
            </div>
            {msg.role === 'assistant' && (
              <div className="flex items-center gap-1 mt-1">
                <button
                  onClick={() => enviarFeedback(msg.id, 'positivo')}
                  disabled={!!feedbackDado[msg.id]}
                  className={`p-1 rounded-lg transition-colors ${feedbackDado[msg.id] === 'positivo' ? 'text-emerald-600 bg-emerald-50' : 'text-gray-300 hover:text-emerald-600'}`}
                  data-testid={`nau-floating-feedback-positivo-${idx}`}
                  title="Resposta útil"
                >
                  <ThumbsUp className="w-3 h-3" />
                </button>
                <button
                  onClick={() => enviarFeedback(msg.id, 'negativo')}
                  disabled={!!feedbackDado[msg.id]}
                  className={`p-1 rounded-lg transition-colors ${feedbackDado[msg.id] === 'negativo' ? 'text-red-600 bg-red-50' : 'text-gray-300 hover:text-red-600'}`}
                  data-testid={`nau-floating-feedback-negativo-${idx}`}
                  title="Resposta não ajudou"
                >
                  <ThumbsDown className="w-3 h-3" />
                </button>
              </div>
            )}
            {msg.role === 'assistant' && msg.sugerir_chamado && (
              <div className="mt-1">
                {chamadosAbertos[msg.id] ? (
                  <span className="text-[11px] text-emerald-600 font-medium">✓ Chamado aberto</span>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50 px-2"
                    onClick={() => abrirDialogoChamado(msg, idx - 1)}
                    data-testid={`nau-floating-abrir-chamado-btn-${idx}`}
                  >
                    <LifeBuoy className="w-3 h-3 mr-1" /> Abrir Chamado
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Loader2 className="w-3 h-3 animate-spin" /> Analisando...
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-2 border-t border-gray-100 bg-white flex-shrink-0">
        <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleSelecionarImagem} data-testid="nau-floating-imagem-input" />
        {imagemBase64 && (
          <div className="relative inline-block mb-1.5" data-testid="nau-floating-imagem-preview">
            <img src={imagemBase64} alt="Pré-visualização" className="h-12 rounded-lg border border-gray-200" />
            <button type="button" onClick={() => setImagemBase64(null)} className="absolute -top-1.5 -right-1.5 bg-gray-800 text-white rounded-full p-0.5">
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="h-9 w-9 flex-shrink-0 rounded-lg border border-gray-200 text-gray-400 hover:text-blue-600 flex items-center justify-center"
            title="Anexar print de erro"
            data-testid="nau-floating-anexar-btn"
          >
            <Paperclip className="w-3.5 h-3.5" />
          </button>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Pergunte algo..."
            className="flex-1 h-9 px-3 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-300"
            disabled={sending}
            data-testid="nau-floating-input"
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={sending || (!input.trim() && !imagemBase64)}
            className="h-9 w-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center disabled:opacity-40"
            data-testid="nau-floating-send"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Dialog: Abrir Chamado */}
      <Dialog open={chamadoDialogOpen} onOpenChange={setChamadoDialogOpen}>
        <DialogContent data-testid="nau-floating-chamado-dialog">
          <DialogHeader>
            <DialogTitle>Abrir Chamado de Suporte</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              O Nau não conseguiu resolver totalmente sua dúvida. Vamos encaminhar esta conversa para o time de suporte.
            </p>
            <div>
              <Label>Tipo de Chamado</Label>
              <p className="text-[11px] text-teal-600 mb-1">Classificação sugerida automaticamente pelo Nau — ajuste se necessário.</p>
              <Select value={chamadoTipo} onValueChange={setChamadoTipo}>
                <SelectTrigger data-testid="nau-floating-chamado-tipo-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_CHAMADO.map((t) => (
                    <SelectItem key={t.valor} value={t.valor}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChamadoDialogOpen(false)} data-testid="nau-floating-chamado-cancelar-btn">
              Cancelar
            </Button>
            <Button onClick={confirmarAbrirChamado} disabled={criandoChamado} data-testid="nau-floating-chamado-confirmar-btn">
              {criandoChamado ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <LifeBuoy className="w-4 h-4 mr-1" />}
              Abrir Chamado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
